// RB-MY01-PalmGarden.web/src/components/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  People as PeopleIcon,
  LocalFlorist as PlantIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  PhotoLibrary as PhotoIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { GridContainer, GridItem } from '../components/CustomGrid';

interface DashboardStats {
  totalPlants: number;
  publishedPlants: number;
  unpublishedPlants: number;
  totalCategories: number;
  recentActivity: ActivityItem[];
  needsAttention: PlantItem[];
}

interface ActivityItem {
  id: string;
  type: 'plant_added' | 'plant_published' | 'plant_updated' | 'user_registered';
  title: string;
  description: string;
  timestamp: string;
  icon: React.ReactNode;
}

interface PlantItem {
  id: number;
  name: string;
  type: string;
  published: boolean;
  imageUrl?: string;
  lastUpdated: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const api = useApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const plants = await api.getPlants();
      
      const totalPlants = plants.length;
      const publishedPlants = plants.filter(p => p.published).length;
      const unpublishedPlants = totalPlants - publishedPlants;
      const categories = new Set(plants.map(p => p.type)).size;
      
      // Mock recent activity
      const recentActivity: ActivityItem[] = [
        {
          id: '1',
          type: 'plant_added',
          title: 'New plant added',
          description: 'Heliconia rostrata was added to the database',
          timestamp: new Date().toISOString(),
          icon: <PlantIcon color="primary" />
        },
        {
          id: '2',
          type: 'plant_published',
          title: 'Plant published',
          description: 'Bird of Paradise is now live on the public site',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          icon: <PublishIcon color="success" />
        }
      ];

      // Plants that need attention (unpublished or recently updated)
      const needsAttention = plants
        .filter(p => !p.published || new Date(p.updatedAt) > new Date(Date.now() - 86400000))
        .slice(0, 5)
        .map(p => ({
          id: p.id,
          name: p.name,
          type: p.type,
          published: p.published,
          imageUrl: p.imageUrl,
          lastUpdated: p.updatedAt
        }));

      setStats({
        totalPlants,
        publishedPlants,
        unpublishedPlants,
        totalCategories: categories,
        recentActivity,
        needsAttention
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'add_plant':
        navigate('/admin/plants/add');
        break;
      case 'manage_plants':
        navigate('/admin/plants');
        break;
      case 'publish_all':
        // TODO: Implement bulk publish
        break;
      case 'settings':
        navigate('/admin/settings');
        break;
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchDashboardData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!stats) return null;

  const publishPercentage = stats.totalPlants > 0 
    ? Math.round((stats.publishedPlants / stats.totalPlants) * 100) 
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🌿 Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Welcome back! Here's what's happening with your plant database.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleQuickAction('add_plant')}
            sx={{ fontWeight: 'bold' }}
          >
            Add Plant
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <GridContainer spacing={3} sx={{ mb: 4 }}>
        <GridItem xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlantIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats.totalPlants}
                  </Typography>
                  <Typography variant="body2">Total Plants</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </CardContent>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PublishIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats.publishedPlants}
                  </Typography>
                  <Typography variant="body2">Published</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={publishPercentage} 
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </CardContent>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UnpublishIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats.unpublishedPlants}
                  </Typography>
                  <Typography variant="body2">Unpublished</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={100 - publishPercentage} 
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </CardContent>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats.totalCategories}
                  </Typography>
                  <Typography variant="body2">Categories</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </CardContent>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            🚀 Quick Actions
          </Typography>
          <GridContainer spacing={2}>
            <GridItem xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleQuickAction('add_plant')}
                sx={{ py: 2 }}
              >
                Add New Plant
              </Button>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PlantIcon />}
                onClick={() => handleQuickAction('manage_plants')}
                sx={{ py: 2 }}
              >
                Manage Plants
              </Button>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PublishIcon />}
                onClick={() => handleQuickAction('publish_all')}
                sx={{ py: 2 }}
              >
                Publish All
              </Button>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => handleQuickAction('settings')}
                sx={{ py: 2 }}
              >
                Settings
              </Button>
            </GridItem>
          </GridContainer>
        </CardContent>
      </Card>

      {/* Content Grid */}
      <GridContainer spacing={3}>
        {/* Recent Activity */}
        <GridItem xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                📊 Recent Activity
              </Typography>
              <List>
                {stats.recentActivity.map((activity) => (
                  <ListItem key={activity.id} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'grey.100' }}>
                        {activity.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {activity.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </GridItem>

        {/* Needs Attention */}
        <GridItem xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                ⚠️ Needs Attention
              </Typography>
              {stats.needsAttention.length > 0 ? (
                <List>
                  {stats.needsAttention.map((plant) => (
                    <ListItem key={plant.id} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar src={plant.imageUrl} sx={{ width: 40, height: 40 }}>
                          <PlantIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={plant.name}
                        secondary={
                          <Box>
                            <Chip 
                              label={plant.type} 
                              size="small" 
                              sx={{ mr: 1 }}
                            />
                            <Chip 
                              label={plant.published ? 'Published' : 'Draft'} 
                              size="small" 
                              color={plant.published ? 'success' : 'warning'}
                            />
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/admin/plants/${plant.id}`)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/admin/plants/edit/${plant.id}`)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  All plants are up to date! 🎉
                </Typography>
              )}
            </CardContent>
          </Card>
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default AdminDashboard; 